import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Pencil, X, Eye, EyeOff, UserCheck, UserX } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { USER_ADMIN_FIELDS, USERS_TABLE } from "@/lib/userAccess";

export function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    role: "vendedor",
    cod_rep: "",
    comision: 0,
    ver_comisiones_globales: false,
    active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from(USERS_TABLE)
        .select(USER_ADMIN_FIELDS)
        .order('name', { ascending: true });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los usuarios",
        variant: "destructive"
      });
    }
  };

  const callUserFunction = async (functionName, payload) => {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session?.access_token) {
      throw new Error("Sesión no válida. Vuelve a iniciar sesión.");
    }

    const response = await fetch(`${supabase.supabaseUrl}/functions/v1/${functionName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabase.supabaseKey,
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "No se pudo completar la operación");
    }
    return data;
  };

  const buildUserPayload = (id = null) => ({
    ...(id ? { id } : {}),
    username: formData.username.trim().toLowerCase(),
    password: formData.password,
    name: formData.name,
    role: formData.role,
    cod_rep: formData.cod_rep,
    comision: formData.comision,
    ver_comisiones_globales: formData.ver_comisiones_globales,
    active: formData.active
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (editingUserId) {
        await callUserFunction("update-user", buildUserPayload(editingUserId));

        toast({
          title: "Usuario actualizado",
          description: "El usuario se ha actualizado exitosamente",
        });
        setEditingUserId(null);
      } else {
        await callUserFunction("create-user", buildUserPayload());

        toast({
          title: "Usuario creado",
          description: "El usuario se ha creado exitosamente",
        });
        setShowNewForm(false);
      }

      await fetchUsers();
      resetForm();
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el usuario",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      name: "",
      role: "vendedor",
      cod_rep: "",
      comision: 0,
      ver_comisiones_globales: false,
      active: true
    });
    setShowPassword(false);
  };

  const handleEdit = (user) => {
    setEditingUserId(user.id);
    setFormData({
      username: user.username,
      password: user.password,
      name: user.name,
      role: user.role,
      cod_rep: user.cod_rep || "",
      comision: user.comision || 0,
      ver_comisiones_globales: user.ver_comisiones_globales || false,
      active: user.active !== false
    });
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    resetForm();
  };

  const handleToggleActive = async (userToUpdate) => {
    try {
      if (userToUpdate.role === "admin") {
        toast({
          title: "Error",
          description: "No se puede dar de baja a un administrador desde esta pantalla",
          variant: "destructive"
        });
        return;
      }

      const nextActive = userToUpdate.active === false;
      const action = nextActive ? "reactivar" : "dar de baja";
      const confirmed = window.confirm(
        `¿Seguro que quieres ${action} al usuario "${userToUpdate.name}"?`
      );

      if (!confirmed) return;

      setIsSubmitting(true);
      await callUserFunction("update-user", {
        id: userToUpdate.id,
        username: userToUpdate.username,
        password: userToUpdate.password,
        name: userToUpdate.name,
        role: userToUpdate.role,
        cod_rep: userToUpdate.cod_rep || "",
        comision: userToUpdate.comision || 0,
        ver_comisiones_globales: userToUpdate.ver_comisiones_globales || false,
        active: nextActive
      });

      await fetchUsers();
      toast({
        title: nextActive ? "Usuario reactivado" : "Usuario dado de baja",
        description: `El usuario "${userToUpdate.name}" se ha actualizado correctamente`,
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo actualizar el estado del usuario",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderUserForm = (isNewUser = false) => (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      onSubmit={handleSubmit}
      className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium mb-1">Usuario</label>
          <Input
            required
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Contraseña</label>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="pr-10"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-500" />
              ) : (
                <Eye className="h-4 w-4 text-gray-500" />
              )}
            </Button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nombre</label>
          <Input
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Rol</label>
          <select
            className="w-full rounded-md border border-input bg-background px-3 py-2"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          >
            <option value="vendedor">Vendedor</option>
            <option value="cliente">Cliente</option>
            <option value="admin">Administrador</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Código de Representante</label>
          <Input
            value={formData.cod_rep}
            onChange={(e) => setFormData({ ...formData, cod_rep: e.target.value })}
            placeholder="Código del representante"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Comisión (%)</label>
          <Input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={formData.comision}
            onChange={(e) => setFormData({ ...formData, comision: parseFloat(e.target.value) || 0 })}
            placeholder="Porcentaje de comisión"
          />
        </div>
        <div className="md:col-span-2">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="ver_comisiones_globales"
              checked={formData.ver_comisiones_globales}
              onChange={(e) => setFormData({ ...formData, ver_comisiones_globales: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="ver_comisiones_globales" className="text-sm font-medium">
              Ver comisiones globales
            </label>
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="active" className="text-sm font-medium">
              Usuario activo
            </label>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={isNewUser ? () => setShowNewForm(false) : handleCancelEdit}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Guardando..." : editingUserId ? "Actualizar" : "Crear"}
        </Button>
      </div>
    </motion.form>
  );

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Gestión de Usuarios</h2>
          <Button 
            onClick={() => {
              setShowNewForm(!showNewForm);
              setEditingUserId(null);
              resetForm();
            }}
            variant={showNewForm ? "outline" : "default"}
          >
            <Plus className="mr-2 h-4 w-4" /> 
            {showNewForm ? "Cancelar" : "Nuevo Usuario"}
          </Button>
        </div>

        <AnimatePresence>
          {showNewForm && renderUserForm(true)}
        </AnimatePresence>

        <div className="grid gap-4">
          {users.map((user) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <h3 className="font-semibold">{user.name}</h3>
                  <p className="text-sm text-gray-600">Usuario: {user.username}</p>
                  <p className="text-sm text-gray-600">Rol: {user.role}</p>
                  {user.cod_rep && (
                    <p className="text-sm text-gray-600">Código: {user.cod_rep}</p>
                  )}
                  <p className="text-sm text-gray-600">Comisión: {user.comision}%</p>
                  <p className="text-sm text-gray-600">
                    Ver comisiones globales: {user.ver_comisiones_globales ? "Sí" : "No"}
                  </p>
                  <p className={`text-sm font-medium ${user.active === false ? "text-red-600" : "text-green-700"}`}>
                    Estado: {user.active === false ? "Baja" : "Activo"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleEdit(user)}
                    disabled={user.id === "admin"}
                  >
                    {editingUserId === user.id ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant={user.active === false ? "outline" : "destructive"}
                    size="icon"
                    onClick={() => handleToggleActive(user)}
                    disabled={user.role === "admin" || isSubmitting}
                  >
                    {user.active === false ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <AnimatePresence>
                {editingUserId === user.id && renderUserForm(false)}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
